/**
 * CSS injected into the embedded DocuSeal builder. The builder renders in a shadow
 * root, so it cannot inherit our CSS variables: colors are the app tokens spelled out.
 */

import {
  SERVER_VARIABLE_ATTRIBUTE,
  UNPLACED_SERVER_VARIABLE_ATTRIBUTE,
} from "@/pages/admin/useServerVariableMarks";

const APP_FONT = '"DM Sans", ui-sans-serif, system-ui, sans-serif';

/**
 * Lucide "braces", in place of the field-type glyph on server-filled variables.
 * DocuSeal picks that glyph from the field's `type` and has no icon property, so it is
 * painted over with CSS. Only the alpha of the image matters: it is a mask.
 */
const VARIABLE_ICON_URL = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1"/>' +
    '<path d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1"/></svg>'
)}")`;

/**
 * Temporary: DocuSeal has no tab-label prop; both tabs share .custom-fields-tab.
 * Also marks the server-filled variables with their own icon.
 */
export const DOCUSEAL_VARIABLES_TAB_CSS = `
.custom-fields-tabs .custom-fields-tab:first-child,
.custom-fields-tabs .custom-fields-tab:last-child {
  font-size: 0 !important;
}
.custom-fields-tabs .custom-fields-tab:first-child::after,
.custom-fields-tabs .custom-fields-tab:last-child::after {
  font-size: 0.75rem;
  line-height: 1.5rem;
  font-weight: 500;
}
.custom-fields-tabs .custom-fields-tab:first-child::after {
  content: "Custom Inputs";
}
.custom-fields-tabs .custom-fields-tab:last-child::after {
  content: "Values";
}
@media (min-width: 768px) {
  .main-container {
    height: calc(100% - 60px);
    min-height: 0;
  }
  .fields-list-container {
    display: flex !important;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .fields-list-container > div {
    display: flex;
    height: 100%;
    min-height: 0;
    flex-direction: column;
  }
  /* DocuSeal's draw prompt sits in a sticky inset-0 h-full wrapper, and this
     sidebar is a column flex. Left alone, the wrapper either opens a
     sidebar-height hole or shrinks until the prompt covers the tabs. Keep the
     prompt's own height and stick it to the top of the list. */
  .fields-list-container > div:has(> .draw-field-container) {
    display: block;
    position: sticky !important;
    top: 0 !important;
    right: auto !important;
    bottom: auto !important;
    left: auto !important;
    flex: 0 0 auto !important;
    height: auto !important;
    min-height: min-content !important;
    overflow: visible !important;
  }
  /* Placed fields grow naturally to 60%, then scroll. With few or no placed
     fields this collapses to content height and leaves most space to Values. */
  .fields-list-container .fields {
    flex: 0 1 auto;
    min-height: 0;
    max-height: 60%;
    overflow-y: auto;
    scrollbar-gutter: stable;
  }
  .fields-list-container .custom-fields-tabs {
    flex: 0 0 auto;
  }
  /* Custom Inputs remains content-sized rather than stretching like Values. */
  .fields-list-container .custom-fields-tabs + .fields-grid {
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
  /* Values stacks search + list. It grows with its rows up to 40% of the sidebar
     (the editor height), then the list scrolls. */
  .fields-list-container .custom-fields-tabs + div:not(.fields-grid) {
    display: flex;
    flex: 0 1 auto;
    flex-direction: column;
    max-height: 40%;
    min-height: 0;
    min-width: 0;
  }
  /* overflow-y only. overflow-x: hidden plus a z-index here cancels a Values
     drag before it reaches the PDF. Open settings menus leave this scroller
     via the top layer instead of raising the row. */
  .fields-list-container .custom-fields-tabs + div > div.overflow-auto {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    max-height: none !important;
    overflow-y: auto;
    scrollbar-gutter: stable;
  }
}
/* Everything in Values is server-filled. A placed row is one of ours only when it carries
   ${SERVER_VARIABLE_ATTRIBUTE} (set by useServerVariableMarks, which matches its name).
   Any other field, such as one the signer fills or the signing date, keeps its type glyph. */
.default-field.fields-list-item .field-types-dropdown > label svg,
.fields .list-field[${SERVER_VARIABLE_ATTRIBUTE}] .field-types-dropdown > label svg {
  display: none;
}
.default-field.fields-list-item .field-types-dropdown > label::before,
.fields .list-field[${SERVER_VARIABLE_ATTRIBUTE}] .field-types-dropdown > label::before {
  content: "";
  display: block;
  width: 20px;
  height: 20px;
  background: currentColor;
  -webkit-mask: ${VARIABLE_ICON_URL} center / contain no-repeat;
  mask: ${VARIABLE_ICON_URL} center / contain no-repeat;
}
/* The same on the PDF: the type glyph in the toolbar of a selected box, and the watermark
   that fills an empty box (an svg in a span; the span is what carries the glyph size). */
.field-area-container[${SERVER_VARIABLE_ATTRIBUTE}] .field-area-controls .field-types-dropdown > label svg,
.field-area-container[${SERVER_VARIABLE_ATTRIBUTE}] .field-area > span > svg.opacity-50 {
  display: none;
}
.field-area-container[${SERVER_VARIABLE_ATTRIBUTE}] .field-area-controls .field-types-dropdown > label::before {
  content: "";
  display: block;
  width: 27px;
  height: 20px;
  background: currentColor;
  -webkit-mask: ${VARIABLE_ICON_URL} center / contain no-repeat;
  mask: ${VARIABLE_ICON_URL} center / contain no-repeat;
}
/* The span shrink-wraps its svg, so it needs a width of its own once the svg is hidden. */
.field-area-container[${SERVER_VARIABLE_ATTRIBUTE}] .field-area > span:has(> svg.opacity-50) {
  width: 100%;
}
.field-area-container[${SERVER_VARIABLE_ATTRIBUTE}] .field-area > span:has(> svg.opacity-50)::before {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  max-height: 40px;
  opacity: 0.5;
  background: currentColor;
  -webkit-mask: ${VARIABLE_ICON_URL} center / contain no-repeat;
  mask: ${VARIABLE_ICON_URL} center / contain no-repeat;
}
/* A field created from a server variable has no useful settings until it has
   an area on the document. Suppress the empty popover, not the draggable row. */
.fields .list-field[${UNPLACED_SERVER_VARIABLE_ATTRIBUTE}] .field-settings-dropdown {
  display: none !important;
}
`.trim();

/** Concrete app tokens: the builder shadow root cannot inherit our CSS variables. */
export const DOCUSEAL_APP_THEME_CSS = `
.ds {
  color: hsl(222 47% 11%);
  font-family: ${APP_FONT};
}
.fields-list-container {
  background: white;
  color: hsl(222 47% 11%);
}
.roles-dropdown > label,
.fields-list-item,
.default-field,
.field-type-button {
  border-color: hsl(214 32% 91%) !important;
  border-radius: 0.625rem !important;
  background: white !important;
}
.roles-dropdown > label:hover,
.fields-list-item:hover,
.default-field:hover,
.field-type-button:hover {
  border-color: hsl(234 70% 62%) !important;
  background: hsl(234 70% 96%) !important;
  color: hsl(234 70% 42%) !important;
}
.base-button {
  min-height: 2.25rem !important;
  height: 2.25rem !important;
  padding: 0 1rem !important;
  border-radius: 0.375rem !important;
  border: 1px solid hsl(214 32% 91%) !important;
  background: white !important;
  color: hsl(222 47% 11%) !important;
  font-family: ${APP_FONT} !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  line-height: 1.25rem !important;
  text-transform: none !important;
  animation: none !important;
}
.base-button:hover {
  background: hsl(210 40% 96%) !important;
}
/* Same box as the app's <Button size="sm"> (Save in the Configuration card). */
.base-button.save-button {
  height: 2rem !important;
  min-height: 2rem !important;
  padding: 0 0.75rem !important;
  gap: 0.5rem !important;
  border: 0 !important;
  border-radius: calc(0.625rem - 2px) !important;
  background: hsl(234 70% 62%) !important;
  color: white !important;
  font-size: 0.75rem !important;
  line-height: 1rem !important;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important;
}
.base-button.save-button svg {
  display: none !important;
}
.base-button.save-button:hover {
  background: hsl(234 70% 62% / 0.9) !important;
}
.base-button.save-button:active {
  transform: scale(0.97);
}
.base-button.save-button.disabled {
  opacity: 0.5;
  pointer-events: none;
}
.base-button:focus-visible,
.fields-list-container input:focus-visible,
.roles-dropdown > label:focus-visible {
  outline: 2px solid hsl(234 70% 62%) !important;
  outline-offset: 2px;
}
.fields-list-container .dropdown,
.fields-list-container .dropdown-content,
.fields-list-container [role="menu"] {
  z-index: 50 !important;
}
.field-settings-dropdown input.toggle.toggle-xs,
.field-area-settings-dropdown input.toggle.toggle-xs,
.fields .list-field input.checkbox.checkbox-xs {
  appearance: none !important;
  position: relative;
  width: 2rem !important;
  min-width: 2rem !important;
  height: 1.125rem !important;
  padding: 0 !important;
  border: 1px solid hsl(215 16% 47%) !important;
  border-radius: 9999px !important;
  background: hsl(210 40% 98%) !important;
  box-shadow: none !important;
  cursor: pointer;
}
.field-settings-dropdown input.toggle.toggle-xs::before,
.field-area-settings-dropdown input.toggle.toggle-xs::before,
.fields .list-field input.checkbox.checkbox-xs::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background: hsl(215 16% 35%);
  transition: transform 150ms ease, background-color 150ms ease;
}
.field-settings-dropdown input.toggle.toggle-xs:checked,
.field-area-settings-dropdown input.toggle.toggle-xs:checked,
.fields .list-field input.checkbox.checkbox-xs:checked {
  border-color: hsl(234 70% 62%) !important;
  background: hsl(234 70% 62%) !important;
}
.field-settings-dropdown input.toggle.toggle-xs:checked::before,
.field-area-settings-dropdown input.toggle.toggle-xs:checked::before,
.fields .list-field input.checkbox.checkbox-xs:checked::before {
  transform: translateX(0.875rem);
  background: white;
}
.field-settings-dropdown input.toggle.toggle-xs:disabled,
.field-area-settings-dropdown input.toggle.toggle-xs:disabled,
.fields .list-field input.checkbox.checkbox-xs:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
/* Tabs as chips: DocuSeal's underlined tab-bordered becomes a pill, filled when active. */
.fields-list-container .custom-fields-tab {
  width: auto;
  height: 1.5rem;
  padding: 0 0.75rem;
  border: 1px solid hsl(214 32% 91%) !important;
  border-radius: 9999px;
  color: hsl(215 16% 47%) !important;
  background: white;
  cursor: pointer;
}
.fields-list-container .custom-fields-tab:hover {
  background: hsl(210 40% 96%);
}
.fields-list-container .custom-fields-tab.tab-active {
  border-color: hsl(234 70% 62%) !important;
  background: hsl(234 70% 96%);
  color: hsl(234 70% 42%) !important;
}
/* Placed fields card. Hidden while empty so a bare border never shows. */
.fields-list-container .fields {
  box-sizing: border-box;
  margin: 0 0 0.5rem;
  padding: 0.5rem 0.5rem 0.125rem;
  border: 1px solid hsl(214 32% 91%);
  border-radius: 0.625rem;
  background: white;
}
.fields-list-container .fields:empty {
  display: none;
}
/* Party picker: the reset above dropped the builder's gap under it, and it gets the
   same caption type as the Custom Inputs / Values tabs. The builder lays the picker
   row out as flex items (gap 0.25rem), so the caption wraps onto its own line. */
.fields-list-container div:has(> .roles-dropdown) {
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}
.fields-list-container div:not(.sticky):has(> .roles-dropdown) {
  position: relative;
}
/* DocuSeal's sticky row is its own stacking context (z-index 1), so its menu's
   z-index only counts inside it. While the menu is in use (it opens on hover and on
   keyboard focus), lift the row above the field list, whose row type menus are at 50,
   so the open menu covers them; otherwise the row stays under DocuSeal's overlays. */
.fields-list-container div:has(> .roles-dropdown):is(:hover, :focus-within) {
  z-index: 60;
}
.fields-list-container div:has(> .roles-dropdown)::before {
  content: "Editing fields for";
  flex: 0 0 100%;
  color: hsl(215 16% 47%);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
}
/* The tab row and the panel under it are siblings DocuSeal gives no wrapper, so the
   card is drawn as header + body: the tabs get the top border and corners, the active
   panel (Custom Inputs grid or Values) the rest. */
.fields-list-container .custom-fields-tabs {
  display: flex;
  gap: 0.375rem;
  margin-bottom: 0 !important;
  padding: 0.5rem 0.5rem 0;
  border: 1px solid hsl(214 32% 91%);
  border-bottom: 0 !important;
  border-radius: 0.625rem 0.625rem 0 0;
  background: white;
}
.fields-list-container .custom-fields-tabs + .fields-grid,
.fields-list-container .custom-fields-tabs + div:not(.fields-grid) {
  box-sizing: border-box;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border: 1px solid hsl(214 32% 91%);
  border-top: 0;
  border-radius: 0 0 0.625rem 0.625rem;
  background: white;
}
.fields-list-container input {
  height: 2.25rem !important;
  padding: 0.5rem 0.75rem !important;
  border: 1px solid hsl(214 32% 91%) !important;
  border-radius: 0.625rem !important;
  background: white !important;
  color: hsl(222 47% 11%) !important;
  font-size: 0.875rem !important;
}
.fields-list-container input::placeholder {
  color: hsl(215 16% 47%) !important;
}
.fields-list-container hr {
  border-color: hsl(214 32% 91%) !important;
}
.field-remove-button:hover {
  color: hsl(0 84% 60%) !important;
}
`.trim();

/**
 * Hides the party pickers (sidebar, and the toolbar of a selected box on the PDF, which
 * also offers "Add Second Party"), for a template whose parties the editor leaves alone
 * (see `builderParties`).
 */
export const DOCUSEAL_HIDE_PARTY_PICKER_CSS = `
.fields-list-container div:has(> .roles-dropdown),
.field-area-controls .roles-dropdown {
  display: none !important;
}
`.trim();

export const DOCUSEAL_BUILDER_TITLE_CSS = `
.title-container .template-name {
  display: none !important;
}
`.trim();

/**
 * Hides the builder's own Save. The page's Save drives it (see `useBuilderBridge`), so
 * the two never disagree about whether there is anything to save.
 */
export const DOCUSEAL_HIDE_BUILDER_SAVE_CSS = `
.base-button.save-button {
  display: none !important;
}
`;
